/**
 * Cache Service Utility
 *
 * Handles in-memory caching and deduplication of API requests.
 * Uses patterns from Anandi Website for global persistence and configuration.
 */

// We attach to global to ensure persistence across module re-evaluations
const getGlobal = () => {
  if (typeof window !== "undefined") return window;
  if (typeof global !== "undefined") return global;
  if (typeof self !== "undefined") return self;
  return {};
};

const g = getGlobal();
const globalCache = g._manualCache || new Map();
if (!g._manualCache) g._manualCache = globalCache;

const inFlightRequests = g._inFlightRequests || {};
if (!g._inFlightRequests) g._inFlightRequests = inFlightRequests;

const MAX_CACHE_SIZE = 100; // Adjust based on memory availability
export const IS_SERVER = typeof window === "undefined";

const envTtl = Number(process.env.NEXT_PUBLIC_CACHE_TTL);
export const DEFAULT_CACHE_TTL = isNaN(envTtl) ? 300 : envTtl;

const SERVER_DEDUPE_TTL = 10; // 10 seconds for server-side deduplication

/**
 * Sorts object keys recursively to ensure consistent JSON stringification.
 */
function sortObjectKeys(obj) {
  if (obj === null || typeof obj !== "object" || Array.isArray(obj)) {
    return obj;
  }
  return Object.keys(obj)
    .sort()
    .reduce((acc, key) => {
      acc[key] = sortObjectKeys(obj[key]);
      return acc;
    }, {});
}

/**
 * Generates a unique cache key based on URL, Method, and Payload.
 */
export function generateCacheKey(url, method = "GET", payload = null) {
  const parts = [method.toUpperCase(), url];
  if (payload) {
    // Normalize payload to ensure same keys in different order produce same key
    const normalizedPayload = sortObjectKeys(payload);
    parts.push(JSON.stringify(normalizedPayload));
  }
  return parts.join("|");
}

/**
 * Wraps a fetch/axios function with caching and in-flight tracking.
 *
 * @param {string} key - Unique cache key
 * @param {Function} fetchFn - Async function that performs the network request
 * @param {number} ttlSeconds - Time-to-live in seconds (default: from env)
 */
export async function withManualCache(
  key,
  fetchFn,
  ttlSeconds = DEFAULT_CACHE_TTL,
) {
  const now = Date.now();

  // On server, we prioritize freshness and only deduplicate within a small window
  const effectiveTTL = IS_SERVER ? Math.min(ttlSeconds, SERVER_DEDUPE_TTL) : ttlSeconds;

  // 1. Check if we have a valid cached response
  if (globalCache.has(key)) {
    const { data, timestamp } = globalCache.get(key);
    if (now - timestamp < effectiveTTL * 1000) {
      if (process.env.NODE_ENV === "development") {
        // console.log(`[Cache Hit] ${key}`);
      }
      return data;
    }
    // console.log(`[Cache Expired] ${key} (TTL: ${effectiveTTL}s)`);
    globalCache.delete(key);
  }

  // 2. Check if a request for this key is already in progress
  if (inFlightRequests[key]) {
    // console.log(`[Deduplicating] ${key}`);
    return inFlightRequests[key];
  }

  // 3. Perform the request and track it
  // console.log(`[Network Request] ${key}`);
  const requestPromise = fetchFn()
    .then((data) => {
      // Maintain size limit (LRU behavior)
      if (globalCache.size >= MAX_CACHE_SIZE) {
        const oldestKey = globalCache.keys().next().value;
        globalCache.delete(oldestKey);
      }

      // Store in cache upon success
      globalCache.set(key, { data, timestamp: Date.now() });
      delete inFlightRequests[key];
      return data;
    })
    .catch((error) => {
      // Clean up in-flight tracker on error
      delete inFlightRequests[key];
      throw error;
    });

  inFlightRequests[key] = requestPromise;
  return requestPromise;
}

// Periodic cleanup of expired entries every 5 minutes
if (typeof setInterval !== "undefined") {
  setInterval(() => {
    const now = Date.now();
    for (const [key, value] of globalCache.entries()) {
      // Cleanup entries older than 1 hour as a safety measure
      if (now - value.timestamp > 3600 * 1000) {
        globalCache.delete(key);
      }
    }
  }, 300 * 1000); // Every 5 minutes
}
